import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { ng } from "../../ngds";
import { SXEditField } from "./SXEditField";

import { useTranslation } from "react-i18next";
import { useAdmin } from "../../slices/adminSlice";
import type { FeatureFlag, FeatureFlagChange } from "../../types/featureFlags";

export interface AdminViewModalProps {
  close: () => void;
  /**
   * Runtime feature flags to offer the user, declared by the host app — this component knows
   * nothing about any particular flag. Omit (or pass an empty list) and the panel is not rendered.
   */
  featureFlags?: FeatureFlag[];
  /** Called when a switch is flipped. The app owns the state; this only reports the intent. */
  onFeatureFlagChange?: FeatureFlagChange;
}

export function AdminViewModal(props: AdminViewModalProps) {
  const [projectGuid, setProjectGuid] = useState("0");
  const [error, setError] = useState("");
  const { t, i18n } = useTranslation();
  const toggleSurvicateVisibility = () => {};
  const { fetchAdmin, downloadProject } = useAdmin();
  const getProjcectData = async () => {
    //  http.formPost("/api/projects/all", { name: "projects.csv" }, true);
  };

  useEffect(() => {
    (async () => {
      let a = await fetchAdmin();
      let payload = a.payload;
      if (payload.rcDesign && payload.column) {
        setAdminReportRC(payload.rcDesign as ng.endpoint_args.common_api_functions.AdminReport);
        setAdminReportFD(payload.column as ng.endpoint_args.common_api_functions.AdminReport);
        setAdminReportAnalysis(payload.analyse as ng.endpoint_args.common_api_functions.AdminReport);
      }
    })();
  }, []);

  const footer = (
    <>
      <Container>
        <Row>
          <Col lg={10}>
            <Button className="modal-btn" onClick={getProjcectData}>
              Export Project Data
            </Button>
          </Col>
          <Col>
            <Button className="modal-btn" onClick={props.close}>
              {t("close_")}
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );

  const [adminReportAnalysis, setAdminReportAnalysis] = useState(
    {} as ng.endpoint_args.common_api_functions.AdminReport,
  );
  const [adminReportFD, setAdminReportFD] = useState({} as ng.endpoint_args.common_api_functions.AdminReport);
  const [adminReportRC, setAdminReportRC] = useState({} as ng.endpoint_args.common_api_functions.AdminReport);

  const getMaterialName = (material: ng.material.Mtrl) => {
    return t(`${material}_`);

    switch (material) {
      case ng.material.Mtrl.MTRL_CONCRETE:
        return t("concrete_");
      case ng.material.Mtrl.MTRL_STEEL:
        return t("steel_");
      case ng.material.Mtrl.MTRL_TIMBER:
        return t("timber_");
      default:
        return t("undefined_");
    }
  };

  useEffect(() => {
    /*
    fillReport("analysis-beam", setAdminReportAnalysis);
    fillReport("fem-design-api", setAdminReportFD);
    fillReport("rc-design", setAdminReportRC);

  */
  }, []);

  // Rendered only when the app declared flags, so an app with none looks exactly as it did.
  const featureFlagPanel = () => {
    const flags = props.featureFlags ?? [];
    if (flags.length === 0) {
      return null;
    }
    return (
      <Row>
        <Col lg={12}>
          <Row className="table-header align-items-center">
            <Col className="text-center table-header-bg">Feature Flags</Col>
          </Row>
          {flags.map((flag) => (
            <Row
              key={flag.key}
              style={{ marginTop: -1 }}
              className="table-body justify-content-center align-items-center"
            >
              <Col>
                {flag.label}
                {flag.description && (
                  <>
                    <br />
                    <small>
                      <i>{flag.description}</i>
                    </small>
                  </>
                )}
              </Col>
              <Col lg={2}>
                <Form.Check
                  type="switch"
                  id={`feature-flag-${flag.key}`}
                  checked={flag.enabled}
                  onChange={(e) => props.onFeatureFlagChange?.(flag.key, e.target.checked)}
                />
              </Col>
            </Row>
          ))}
        </Col>
      </Row>
    );
  };

  const adminReport = (report: ng.endpoint_args.common_api_functions.AdminReport) => {
    return (
      report && (
        <>
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>
              <i>{t("type_of_api_call_.label")}</i>
            </Col>
            <Col>
              <i>{t("count_.label")}</i>
            </Col>
          </Row>
          {report.callCount &&
            report.callCount.map((p, i) => {
              return (
                <Row key={i} style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
                  <Col className="" style={{}}>
                    {getMaterialName(p.material as ng.material.Mtrl)}
                  </Col>
                  <Col className="" style={{}}>
                    <b>{p.count}</b>
                  </Col>
                </Row>
              );
            })}
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>&nbsp;</Col>
            <Col>&nbsp;</Col>
          </Row>

          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>
              <i>{t("calculation_times_.label")}</i>
            </Col>
            <Col>[h:m:s:ms]</Col>
          </Row>
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>{t("average_.label")}</Col>
            <Col>
              <b>{report.calculationTime?.average || "-"}</b>
            </Col>
          </Row>
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>{t("median_.label")}</Col>
            <Col>
              <b>{report.calculationTime?.median || "-"}</b>
            </Col>
          </Row>
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>{t("max_.label")}</Col>
            <Col>
              <b>{report.calculationTime?.max || "-"}</b>
            </Col>
          </Row>
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>{t("min_.label")}</Col>
            <Col>
              <b>{report.calculationTime?.min || "-"}</b>
            </Col>
          </Row>
          <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
            <Col>&nbsp;</Col>
            <Col>&nbsp;</Col>
          </Row>

          {report.errorEntries && report.errorEntries.length > 0 && (
            <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
              <Col>
                <i>{t("error_messages_.label")}:</i>
              </Col>
              <Col>
                <i>{t("count_.label")}</i>
              </Col>
            </Row>
          )}

          {report.errorEntries &&
            report.errorEntries.map((p, i) => {
              return (
                <Row key={i} style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
                  <Col style={{}}>{p.message}</Col>
                  <Col style={{}}>
                    <b>{p.count}</b>
                  </Col>
                </Row>
              );
            })}
        </>
      )
    );
  };

  return (
    <div className="admin_view_box">
      <Container id="ngtable">
        <Row>
          <Col lg={4}>
            <Row className="table-header align-items-center">
              <Col className="text-center table-header-bg">Analysis Beam</Col>
            </Row>
            {adminReport(adminReportAnalysis)}
          </Col>
          <Col lg={4}>
            <Row className="table-header align-items-center">
              <Col className="text-center table-header-bg">FEM-Design API</Col>
            </Row>
            {adminReport(adminReportFD)}
          </Col>
          <Col lg={4}>
            <Row className="table-header align-items-center">
              <Col className="text-center table-header-bg">RC-Design</Col>
            </Row>
            {adminReport(adminReportRC)}
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <Row className="table-header align-items-center">
              <Col className="text-center table-header-bg">Debug Tools</Col>
            </Row>
            <Row style={{ marginTop: -1 }} className="table-body justify-content-center align-items-center">
              <Col className="text-center table-header-bg">
                <Button className="modal-btn" onClick={() => {}}>
                  Show Survicate Survey
                </Button>
              </Col>
            </Row>
          </Col>
          <Col lg={3} className="text-center table-header-bg">
            <SXEditField
              val={projectGuid}
              type="text"
              onUpdate={(value: string, revertValue: () => void) => {
                setProjectGuid(value);
              }}
            />
            <small>{error}</small>
          </Col>
          <Col lg={3} className="text-center table-header-bg">
            <Button
              className="modal-btn"
              onClick={() => {
                downloadProject(projectGuid);
              }}
            >
              Download Project
            </Button>
          </Col>
        </Row>
        {featureFlagPanel()}
      </Container>
    </div>
  );
}
